import React, { useState } from 'react';
import { Bell, Mail, Globe, Plus, Trash2, Dog, Cat, Save } from 'lucide-react';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { Input } from '../ui/Input';
import { mockExtendedUser, PetProfile } from '../../utils/mockAccountData';
import { useNotifications } from '../../context/NotificationsContext';
export function PreferencesSection() {
  const [preferences, setPreferences] = useState(mockExtendedUser.preferences);
  const [pets, setPets] = useState<PetProfile[]>(mockExtendedUser.pets);
  const [isAddingPet, setIsAddingPet] = useState(false);
  const {
    addNotification
  } = useNotifications();
  // New Pet Form State
  const [newPet, setNewPet] = useState<Partial<PetProfile>>({
    name: '',
    species: 'dog',
    breed: '',
    age: 0,
    dietaryNeeds: []
  });
  const handlePreferenceChange = (key: keyof typeof preferences) => {
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key as keyof typeof preferences] // Type assertion for boolean toggle
    }));
  };
  const handleSavePreferences = () => {
    addNotification('success', 'Preferencias guardadas correctamente');
  };
  const handleAddPet = (e: React.FormEvent) => {
    e.preventDefault();
    const petToAdd = {
      ...newPet,
      id: Math.random().toString(36).substr(2, 9)
    } as PetProfile;
    setPets([...pets, petToAdd]);
    setIsAddingPet(false);
    setNewPet({
      name: '',
      species: 'dog',
      breed: '',
      age: 0,
      dietaryNeeds: []
    });
    addNotification('success', 'Mascota añadida correctamente');
  };
  const handleDeletePet = (id: string) => {
    if (confirm('¿Estás seguro de que quieres eliminar este perfil?')) {
      setPets(pets.filter(p => p.id !== id));
      addNotification('success', 'Perfil de mascota eliminado');
    }
  };
  return <div className="space-y-8" data-id="element-420">
      <div data-id="element-421">
        <h2 className="text-xl font-bold text-gray-900" data-id="element-422">Preferencias</h2>
        <p className="text-sm text-gray-500 mt-1" data-id="element-423">
          Personaliza tu experiencia, comunicaciones y perfiles de mascotas.
        </p>
      </div>

      {/* Communications */}
      <Card className="p-6 md:p-8" data-id="element-424">
        <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center" data-id="element-425">
          <Mail className="w-5 h-5 mr-2 text-emerald-600" data-id="element-426" />
          Comunicaciones
        </h3>
        <div className="space-y-6" data-id="element-427">
          <div className="flex items-start justify-between" data-id="element-428">
            <div data-id="element-429">
              <h4 className="font-medium text-gray-900" data-id="element-430">Boletín Informativo</h4>
              <p className="text-sm text-gray-500" data-id="element-431">
                Recibe consejos veterinarios y ofertas exclusivas.
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer" data-id="element-432">
              <input type="checkbox" checked={preferences.newsletter} onChange={() => handlePreferenceChange('newsletter')} className="sr-only peer" data-id="element-433" />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-emerald-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600" data-id="element-434"></div>
            </label>
          </div>

          <div className="border-t border-gray-100 pt-6" data-id="element-435">
            <h4 className="font-medium text-gray-900 mb-4" data-id="element-436">Tipos de correos</h4>
            <div className="space-y-3" data-id="element-437">
              <label className="flex items-center" data-id="element-438">
                <input type="checkbox" checked={preferences.emailUpdates} onChange={() => handlePreferenceChange('emailUpdates')} className="w-4 h-4 text-emerald-600 rounded focus:ring-emerald-500" data-id="element-439" />
                <span className="ml-3 text-sm text-gray-700" data-id="element-440">
                  Actualizaciones de pedidos
                </span>
              </label>
              <label className="flex items-center" data-id="element-441">
                <input type="checkbox" checked={preferences.emailPromotions} onChange={() => handlePreferenceChange('emailPromotions')} className="w-4 h-4 text-emerald-600 rounded focus:ring-emerald-500" data-id="element-442" />
                <span className="ml-3 text-sm text-gray-700" data-id="element-443">
                  Promociones y descuentos
                </span>
              </label>
              <label className="flex items-center" data-id="element-444">
                <input type="checkbox" checked={preferences.emailBlog} onChange={() => handlePreferenceChange('emailBlog')} className="w-4 h-4 text-emerald-600 rounded focus:ring-emerald-500" data-id="element-445" />
                <span className="ml-3 text-sm text-gray-700" data-id="element-446">
                  Artículos del blog y consejos
                </span>
              </label>
            </div>
          </div>
        </div>
      </Card>

      {/* Pet Profiles */}
      <Card className="p-6 md:p-8" data-id="element-447">
        <div className="flex items-center justify-between mb-6" data-id="element-448">
          <h3 className="text-lg font-bold text-gray-900 flex items-center" data-id="element-449">
            <Dog className="w-5 h-5 mr-2 text-emerald-600" data-id="element-450" />
            Mis Mascotas
          </h3>
          {!isAddingPet && <Button size="sm" onClick={() => setIsAddingPet(true)} data-id="element-451">
              <Plus className="w-4 h-4 mr-2" data-id="element-452" /> Añadir Mascota
            </Button>}
        </div>

        {isAddingPet ? <form onSubmit={handleAddPet} className="bg-gray-50 p-6 rounded-xl border border-gray-200 mb-6 animate-fadeIn" data-id="element-453">
            <h4 className="font-bold text-gray-900 mb-4" data-id="element-454">Nueva Mascota</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-455">
              <Input label="Nombre" value={newPet.name} onChange={e => setNewPet({
            ...newPet,
            name: e.target.value
          })} required data-id="element-456" />
              <div data-id="element-457">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-458">
                  Especie
                </label>
                <select value={newPet.species} onChange={e => setNewPet({
              ...newPet,
              species: e.target.value as 'dog' | 'cat' | 'other'
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" data-id="element-459">
                  <option value="dog" data-id="element-460">Perro</option>
                  <option value="cat" data-id="element-461">Gato</option>
                  <option value="other" data-id="element-462">Otro</option>
                </select>
              </div>
              <Input label="Raza" value={newPet.breed} onChange={e => setNewPet({
            ...newPet,
            breed: e.target.value
          })} data-id="element-463" />
              <Input label="Edad (años)" type="number" value={newPet.age} onChange={e => setNewPet({
            ...newPet,
            age: parseInt(e.target.value)
          })} data-id="element-464" />
            </div>
            <div className="flex justify-end gap-3" data-id="element-465">
              <Button type="button" variant="ghost" onClick={() => setIsAddingPet(false)} data-id="element-466">
                Cancelar
              </Button>
              <Button type="submit" data-id="element-467">Guardar Perfil</Button>
            </div>
          </form> : null}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-468">
          {pets.map(pet => <div key={pet.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-xl hover:border-emerald-200 hover:bg-emerald-50/30 transition-colors group" data-id="element-469">
              <div className="flex items-center" data-id="element-470">
                <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center text-emerald-600 mr-4" data-id="element-471">
                  {pet.species === 'cat' ? <Cat className="w-6 h-6" data-id="element-472" /> : <Dog className="w-6 h-6" data-id="element-473" />}
                </div>
                <div data-id="element-474">
                  <h4 className="font-bold text-gray-900" data-id="element-475">{pet.name}</h4>
                  <p className="text-sm text-gray-500" data-id="element-476">
                    {pet.breed} • {pet.age} años
                  </p>
                </div>
              </div>
              <button onClick={() => handleDeletePet(pet.id)} className="p-2 text-gray-400 hover:text-red-600 rounded-lg opacity-0 group-hover:opacity-100 transition-all" data-id="element-477">
                <Trash2 className="w-4 h-4" data-id="element-478" />
              </button>
            </div>)}
        </div>
      </Card>

      {/* Language & Region */}
      <Card className="p-6 md:p-8" data-id="element-479">
        <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center" data-id="element-480">
          <Globe className="w-5 h-5 mr-2 text-emerald-600" data-id="element-481" />
          Idioma y Región
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-482">
          <div data-id="element-483">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-484">
              Idioma
            </label>
            <select className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" defaultValue="es" data-id="element-485">
              <option value="es" data-id="element-486">Español</option>
              <option value="en" data-id="element-487">English</option>
              <option value="pt" data-id="element-488">Português</option>
            </select>
          </div>
          <div data-id="element-489">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-490">
              País
            </label>
            <select className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" defaultValue="ES" data-id="element-491">
              <option value="ES" data-id="element-492">España</option>
              <option value="PT" data-id="element-493">Portugal</option>
              <option value="FR" data-id="element-494">Francia</option>
            </select>
          </div>
        </div>
      </Card>

      <div className="flex justify-end" data-id="element-495">
        <Button size="lg" onClick={handleSavePreferences} data-id="element-496">
          <Save className="w-4 h-4 mr-2" data-id="element-497" />
          Guardar Preferencias
        </Button>
      </div>
    </div>;
}